import { useEffect } from 'react';

// Scroll continuity across the landing ⇄ comparison hop.
//
// The router has no scrollRestoration, and now that the hop is a CLIENT-SIDE
// navigation (see comparison-link) the window simply keeps whatever offset it
// had. Two problems fall out of that:
//   • going TO comparison from deep inside the journey, the short page inherits
//     a huge offset and the browser clamps it to some arbitrary spot;
//   • coming BACK, the landing route remounts and the browser's native restore
//     fires while the document is still SHORT — the pinned journey's height only
//     exists once the conductor has dynamically imported GSAP and ScrollTrigger
//     has built its pin spacer. The clamped value is what lands the user
//     "already scrolled to some other position".
//
// So the position is saved explicitly on the way out and re-applied on the way
// back, but only once the document can actually hold it.
const KEY = 'cbcc:journey-scroll';

/** Remember where the journey was before leaving it. Called from ComparisonLink. */
export function saveJourneyScroll(): void {
	try {
		sessionStorage.setItem(KEY, String(Math.round(window.scrollY)));
	} catch {
		// Private-mode sessionStorage throws; losing the position is acceptable,
		// breaking the navigation is not.
	}
}

/**
 * Re-apply the saved journey position on the landing route, waiting for the pin
 * runway to exist first. Polls on rAF because there is no event for "ScrollTrigger
 * finished building the spacer" — and gives up after a budget so a journey that
 * never grows (reduced-motion, locked/rejected view, a GSAP chunk that failed)
 * just stays at the top instead of hanging or jumping later.
 */
export function useRestoreJourneyScroll(): void {
	useEffect(() => {
		let saved: number | null = null;
		try {
			const raw = sessionStorage.getItem(KEY);
			if (raw !== null) saved = Number(raw);
			sessionStorage.removeItem(KEY); // one-shot: only the trip we just made back
		} catch {
			return;
		}
		if (saved === null || !Number.isFinite(saved) || saved <= 0) return;

		let raf = 0;
		const deadline = performance.now() + 3000;
		const tick = () => {
			// Room for the target position yet? scrollHeight grows the moment the
			// pin spacer lands, so this is the signal we're waiting for.
			const reachable =
				document.documentElement.scrollHeight - window.innerHeight >= saved;
			if (reachable) {
				window.scrollTo(0, saved);
				return;
			}
			if (performance.now() > deadline) return;
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, []);
}
